import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UploadSimple, X, CircleNotch, Camera, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MultiImageUploader — pick multiple photos, upload each to /api/uploads/image, keep an ordered array.
 * Props:
 *   value: string[] of URLs
 *   onChange: (urls: string[]) => void
 *   max: number of photos allowed (default 6)
 *   testid: base testid
 *   label: field label
 */
export default function MultiImageUploader({ value = [], onChange, max = 6, testid = "photos", label = "Photos" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files || !files.length) return;
    const slotsLeft = max - value.length;
    const list = Array.from(files).slice(0, slotsLeft);
    if (list.length === 0) { toast.error(`Max ${max} photos`); return; }
    setBusy(true);
    const newUrls = [...value];
    for (const file of list) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5MB`); continue; }
      if (!file.type.startsWith("image/")) { toast.error(`${file.name} isn't an image`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Upload failed for ${file.name}`);
      }
    }
    onChange(newUrls);
    setBusy(false);
    if (newUrls.length > value.length) toast.success(`${newUrls.length - value.length} photo(s) uploaded`);
  };

  const remove = (i) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const makeCover = (i) => {
    if (i === 0) return;
    const next = [value[i], ...value.filter((_, idx) => idx !== i)];
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-[#5C564D]">{label}</div>
        <div className="text-xs text-[#5C564D]">{value.length} / {max}</div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((url, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#E5D9CC]" data-testid={`${testid}-item-${i}`}>
            <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            {i === 0 && (
              <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-[#F9F6F0] flex items-center gap-1" style={{ background: "#D95A41" }}>
                <Star size={10} weight="fill" /> Cover
              </div>
            )}
            <div className="absolute bottom-1 right-1 flex gap-1">
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => makeCover(i)}
                  data-testid={`${testid}-cover-${i}`}
                  className="w-6 h-6 rounded-full bg-[#F9F6F0] text-[#D95A41] flex items-center justify-center border border-[#E5D9CC]"
                  title="Make cover"
                >
                  <Star size={10} weight="fill" />
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`${testid}-remove-${i}`}
                className="w-6 h-6 rounded-full bg-[#1C1B1A] text-[#F9F6F0] flex items-center justify-center"
                title="Remove"
              >
                <X size={10} weight="bold" />
              </button>
            </div>
          </div>
        ))}

        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`${testid}-add`}
            className="aspect-square rounded-xl border-2 border-dashed border-[#E5D9CC] bg-[#F2EBE5] flex flex-col items-center justify-center text-[#5C564D] hover:bg-[#EFE4D3]"
            style={{ transition: "background-color 180ms ease-out" }}
          >
            {busy ? (
              <CircleNotch size={22} className="animate-spin" />
            ) : (
              <>
                <UploadSimple size={22} />
                <span className="text-[10px] mt-1 font-semibold">Add photo</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
      />
      <div className="text-xs text-[#5C564D] mt-2">Up to {max} photos, 5MB each. The first photo is the cover. We auto-optimize each image for fast loading.</div>
    </div>
  );
}
